import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { debounceTime, fromEvent, map, Observable, retry } from "rxjs";
import {
  StudentCourse,
  StudentCourseUpdateTimespent,
} from "../model/student-course.model";
import { StudentCourseLatest } from "../model/student.model";
import { CourseReview } from "../model/course-review.model";

@Injectable({
  providedIn: "root",
})
export class StudentCourseService {
  private apiStudentCoursesUrl = "/student_courses";
  private apiCourseLastedUrl = "/students/course_lasted";
  private apiActiveCourseUrl = "/student_courses/activated/";
  private apiCourseFileUrl = "/course_files";
  private apiCourseReviewUrl = "/course_reviews";

  #http = inject(HttpClient);

  _setFilter(obj: any) {
    return Object.fromEntries(
      Object.entries(obj).filter(([_, v]) => v != null),
    );
  }

  _setParams(value: any) {
    if (!value) return;
    const filter: any = this._setFilter(value);
    let params = new HttpParams();
    for (const key in filter) {
      if (Array.isArray(filter[key])) {
        params = params.append(key, JSON.stringify(filter[key]));
      } else {
        params = params.append(key, filter[key]);
      }
    }
    return params;
  }

  getStudentCourse(filter?: any) {
    const params = this._setParams(filter) || "";
    return this.#http
      .get<any>(`${this.apiStudentCoursesUrl}?${params}`)
      .pipe(retry(1));
  }
  getStudentCourseById(id: number) {
    return this.#http
      .get<any>(`${this.apiStudentCoursesUrl}/${id}`)
      .pipe(retry(1));
  }

  getStudentCourseLasted(filter?: any) {
    const params = this._setParams(filter) || "";
    return this.#http
      .get<any>(`${this.apiCourseLastedUrl}?${params}`)
      .pipe(retry(1));
  }
  updateCourseLatest(payload: StudentCourseLatest): Observable<StudentCourse> {
    return this.#http.post<StudentCourse>("/student_course_latests", payload);
  }

  updateTimeSpent(
    payload: StudentCourseUpdateTimespent,
  ): Observable<StudentCourse> {
    return this.#http.put<StudentCourse>(
      `${this.apiStudentCoursesUrl}/${payload.id}`,
      {
        student_course: payload,
      },
    );
  }

  /**
   * Start a free trial on a course ("ทดลองเรียน").
   *
   * Bypasses the cart entirely: the API writes a zero-price approved
   * transaction and an already-activated trial enrolment whose quota covers only
   * the preview chapters. It refuses when the course exposes no preview video,
   * and when a trial on that course is still running.
   */
  startTrial(courseId: number): Observable<any> {
    return this.#http.post<any>("/trial_enrollments", { course_id: courseId });
  }

  activateCourse(id: any): Observable<any> {
    return this.#http
      .put<any>(`${this.apiActiveCourseUrl}${id}`, {})
      .pipe(retry(1));
  }

  getCourseFiles(filter?: any) {
    const params = this._setParams(filter) || "";
    return this.#http
      .get<any>(`${this.apiCourseFileUrl}?${params}`)
      .pipe(retry(1));
  }

  review(payload: CourseReview): Observable<CourseReview> {
    return this.#http.post<CourseReview>(this.apiCourseReviewUrl, payload);
  }
}
